<template>
  <section class="nx-departments nx-grid-bg" id="departments">
    <div class="nx-departments-container">
      <div class="nx-departments-header nx-animate-on-scroll">
        <p class="nx-departments-subtitle">{{ config?.departments?.subtitle }}</p>
        <h2 class="nx-departments-title">{{ config?.departments?.title }}</h2>
        <p class="nx-departments-desc">{{ config?.departments?.description }}</p>
      </div>

      <!-- AI解决方案分类展示 -->
      <div class="nx-ai-solutions-grid">
        <!-- AI查询 -->
        <div
          v-if="aiSolutions.aiQuery.length"
          class="nx-ai-solution-card nx-ai-query nx-animate-on-scroll"
          @click="showAiDetails('aiQuery')"
        >
          <div class="nx-ai-solution-icon">🔍</div>
          <h3 class="nx-ai-solution-title">AI查询</h3>
          <p class="nx-ai-solution-desc">{{ getAiDesc('aiQuery') }}</p>
          <div class="nx-ai-solution-departments">
            <span
              v-for="dept in aiSolutions.aiQuery"
              :key="dept"
              class="nx-dept-tag"
            >{{ dept }}</span>
          </div>
        </div>

        <!-- AI问数 -->
        <div
          v-if="aiSolutions.aiQueryNum.length"
          class="nx-ai-solution-card nx-ai-querynum nx-animate-on-scroll"
          @click="showAiDetails('aiQueryNum')"
        >
          <div class="nx-ai-solution-icon">📊</div>
          <h3 class="nx-ai-solution-title">AI问数</h3>
          <p class="nx-ai-solution-desc">{{ getAiDesc('aiQueryNum') }}</p>
          <div class="nx-ai-solution-departments">
            <span
              v-for="dept in aiSolutions.aiQueryNum"
              :key="dept"
              class="nx-dept-tag"
            >{{ dept }}</span>
          </div>
        </div>

        <!-- AI审核 -->
        <div
          v-if="aiSolutions.aiReview.length"
          class="nx-ai-solution-card nx-ai-review nx-animate-on-scroll"
          @click="showAiDetails('aiReview')"
        >
          <div class="nx-ai-solution-icon">✅</div>
          <h3 class="nx-ai-solution-title">AI审核</h3>
          <p class="nx-ai-solution-desc">{{ getAiDesc('aiReview') }}</p>
          <div class="nx-ai-solution-departments">
            <span
              v-for="dept in aiSolutions.aiReview"
              :key="dept"
              class="nx-dept-tag"
            >{{ dept }}</span>
          </div>
        </div>

        <!-- AI生成 -->
        <div
          v-if="aiSolutions.aiGeneration.length"
          class="nx-ai-solution-card nx-ai-generation nx-animate-on-scroll"
          @click="showAiDetails('aiGeneration')"
        >
          <div class="nx-ai-solution-icon">🤖</div>
          <h3 class="nx-ai-solution-title">AI生成</h3>
          <p class="nx-ai-solution-desc">{{ getAiDesc('aiGeneration') }}</p>
          <div class="nx-ai-solution-departments">
            <span
              v-for="dept in aiSolutions.aiGeneration"
              :key="dept"
              class="nx-dept-tag"
            >{{ dept }}</span>
          </div>
        </div>

        <!-- AI工具 -->
        <div
          v-if="aiSolutions.aiTools.length"
          class="nx-ai-solution-card nx-ai-tools nx-animate-on-scroll"
          @click="showAiDetails('aiTools')"
        >
          <div class="nx-ai-solution-icon">🛠️</div>
          <h3 class="nx-ai-solution-title">AI工具</h3>
          <p class="nx-ai-solution-desc">{{ getAiDesc('aiTools') }}</p>
          <div class="nx-ai-solution-departments">
            <span
              v-for="dept in aiSolutions.aiTools"
              :key="dept"
              class="nx-dept-tag"
            >{{ dept }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- AI解决方案详情弹窗 -->
    <transition name="modal-fade">
      <div
        v-if="selectedAi"
        class="nx-ai-modal-overlay"
        @click="closeAiDetails"
        @wheel.stop.prevent
      >
        <div
          class="nx-ai-modal"
          :class="selectedAiClass"
          @click.stop
          @wheel.stop
        >
          <div class="nx-ai-modal-header">
            <div class="nx-ai-modal-title-wrapper">
              <span class="nx-ai-modal-icon">{{ getAiIcon(selectedAi) }}</span>
              <h3 class="nx-ai-modal-title">{{ getAiTitle(selectedAi) }}</h3>
            </div>
            <button class="nx-ai-modal-close" @click="closeAiDetails">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div class="nx-ai-modal-content nx-gray-scrollbar" @wheel.stop>
            <!-- AI描述 -->
            <div class="nx-ai-modal-description">
              <p>{{ getAiDesc(selectedAi) }}</p>
            </div>

            <!-- 相关部门列表 -->
            <div class="nx-ai-dept-list">
              <h4 class="nx-ai-dept-list-title">应用部门</h4>
              <div class="nx-ai-dept-cards">
                <div
                  v-for="dept in getDepartmentsForAi(selectedAi)"
                  :key="dept.name"
                  class="nx-ai-dept-card"
                >
                  <h5 class="nx-ai-dept-name">{{ dept.name }}</h5>
                  <p class="nx-ai-dept-desc">{{ getDeptAiDesc(dept, selectedAi) }}</p>
                  <div class="nx-ai-dept-items">
                    <span
                      v-for="(item, idx) in getAiItemsForDept(dept, selectedAi)"
                      :key="'item-' + idx"
                      class="nx-ai-dept-item"
                    >{{ item }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </section>
</template>

<script>
export default {
  name: 'NxDepartments',
  props: {
    config: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      selectedAi: null,
      scrollPosition: 0
    };
  },
  computed: {
    departments() {
      return this.config?.departments?.items || [];
    },
    // 按AI分类整理部门
    aiSolutions() {
      const result = {
        aiQuery: [],
        aiQueryNum: [],
        aiReview: [],
        aiGeneration: [],
        aiTools: []
      };

      this.departments.forEach(dept => {
        // AI查询
        if (dept.aiQuery && dept.aiQuery.length) {
          result.aiQuery.push(dept.name);
        }
        // AI问数
        if (dept.aiQueryNum && dept.aiQueryNum.length) {
          result.aiQueryNum.push(dept.name);
        }
        // AI审核
        if (dept.aiReview && dept.aiReview.length) {
          result.aiReview.push(dept.name);
        }
        // AI生成
        if (dept.aiGeneration && dept.aiGeneration.length) {
          result.aiGeneration.push(dept.name);
        }
        // AI工具
        if (dept.aiTools && dept.aiTools.length) {
          result.aiTools.push(dept.name);
        }
      });

      return result;
    },
    selectedAiClass() {
      if (!this.selectedAi) return '';
      const classMap = {
        'aiQuery': 'ai-query',
        'aiQueryNum': 'ai-querynum',
        'aiReview': 'ai-review',
        'aiGeneration': 'ai-generation',
        'aiTools': 'ai-tools'
      };
      return classMap[this.selectedAi] || '';
    }
  },
  mounted() {
    // 组件已挂载，配置由父组件传递
  },
  methods: {
    getAiDesc(aiType) {
      const descs = {
        'aiQuery': '快速检索知识库、数字工厂等资料，精准定位所需信息',
        'aiQueryNum': '智能分析运营数据、造价匡算、价格比对，实现数据驱动决策',
        'aiReview': '自动审核图纸、文件、标书，提升审查效率与准确性',
        'aiGeneration': '智能生成报告、标书、专篇等文档，大幅提升编制效率',
        'aiTools': '提供翻译、表格结构化、会议听记等实用AI工具'
      };
      return descs[aiType] || '';
    },
    getAiIcon(aiType) {
      const icons = {
        'aiQuery': '🔍',
        'aiQueryNum': '📊',
        'aiReview': '✅',
        'aiGeneration': '🤖',
        'aiTools': '🛠️'
      };
      return icons[aiType] || '';
    },
    getAiTitle(aiType) {
      const titles = {
        'aiQuery': 'AI查询',
        'aiQueryNum': 'AI问数',
        'aiReview': 'AI审核',
        'aiGeneration': 'AI生成',
        'aiTools': 'AI工具'
      };
      return titles[aiType] || '';
    },
    getDepartmentsForAi(aiType) {
      return this.departments.filter(dept => {
        const items = dept[aiType];
        return items && items.length > 0;
      });
    },
    getAiItemsForDept(dept, aiType) {
      return dept[aiType] || [];
    },
    // 部门与AI功能的详细描述映射
    getDeptAiDesc(dept, aiType) {
      const items = dept[aiType] || [];
      const deptName = dept.name;

      // 前期工作部
      if (deptName === '前期工作部') {
        if (aiType === 'aiQuery' && items.includes('知识库')) {
          return '运用AI知识库快速检索历史项目资料，获取同类项目经验数据，为前期规划提供参考支撑';
        }
        if (aiType === 'aiGeneration') {
          const hasSafe = items.includes('安全专篇');
          const hasReport = items.includes('报告生成');
          if (hasSafe && hasReport) {
            return '自动生成可研报告中的安全专篇与环境专篇，基于历史模板智能编制，缩短报告编制周期';
          }
          if (hasSafe) return '基于安全评估报告，AI自动提炼核心内容与合规要求，生成专业安全专篇';
          if (hasReport) return '基于知识库和历史数据，自动生成可研报告，大幅提升编制效率';
        }
        if (aiType === 'aiTools') {
          return '多语言翻译助力海外项目资料查阅，数据预处理工具规范化前期数据格式';
        }
      }

      // 技术部
      if (deptName === '技术部') {
        if (aiType === 'aiQuery') {
          const hasKnowledge = items.includes('知识库');
          const hasDigital = items.includes('数字化交付');
          if (hasKnowledge && hasDigital) {
            return '通过AI知识库快速检索技术文档，数字化交付平台实现设备位号、图纸等信息的智能关联查询';
          }
          if (hasKnowledge) return '快速检索技术规范、标准图纸等技术资料，提升研发效率';
          if (hasDigital) return '基于数字化交付平台，智能查询设备参数、P&ID图纸等技术信息';
        }
        if (aiType === 'aiGeneration' && items.includes('报告生成')) {
          return '基于技术讨论内容，AI自动生成技术报告，提升报告编制效率';
        }
        if (aiType === 'aiTools') {
          const hasMeeting = items.includes('会议听记');
          if (hasMeeting) {
            return '会议听记助手自动记录技术讨论内容，提取要点与待办事项，生成会议纪要';
          }
          return '多语言翻译支持国外技术资料阅读，数据预处理工具规范化技术数据';
        }
      }

      // 采购部
      if (deptName === '采购部') {
        if (aiType === 'aiQuery' && items.includes('知识库')) {
          return '快速查询供应商历史信息、设备参数等采购相关资料，辅助采购决策';
        }
        if (aiType === 'aiQueryNum') {
          const hasType = items.includes('物资种类辨别');
          const hasPrice = items.includes('价格比对');
          const hasData = items.includes('运营数据问数');
          if (hasType && hasPrice && hasData) {
            return 'AI自动识别物资种类，智能分析供应商报价，快速比对历史价格，实现数据化采购决策';
          }
          if (hasType && hasPrice) return '自动辨别物资种类，智能分析供应商报价并比对价格';
          if (hasType) return '依据采购设备特性，AI自动实现物资种类的分类';
          if (hasPrice) return '整合多供应商报价数据，AI自动计算并比对价格';
          if (hasData) return '自然语言查询运营数据，快速获取采购相关信息';
        }
        if (aiType === 'aiGeneration' && items.includes('标书生成')) {
          return 'AI自动解析招标文件，结合企业信息一键生成采购标书，提升投标效率';
        }
        if (aiType === 'aiTools') {
          return '多语言翻译支持进口设备资料阅读，数据预处理规范化采购数据';
        }
      }

      // 市场部
      if (deptName === '市场部') {
        if (aiType === 'aiQuery' && items.includes('知识库')) {
          return '快速查询企业案例、资质证书、技术方案等市场营销资料，支撑客户沟通';
        }
        if (aiType === 'aiQueryNum') {
          const hasType = items.includes('物资种类辨别');
          const hasData = items.includes('运营数据问数');
          if (hasType && hasData) {
            return 'AI分析市场运营数据，自动辨别物资分类，为市场策略提供数据支撑';
          }
          if (hasType) return 'AI辅助辨别物资种类，规范市场产品分类';
          if (hasData) return '自然语言查询市场运营数据，快速获取分析结果';
        }
        if (aiType === 'aiReview') {
          const hasSupplier = items.includes('供应商图纸审核');
          const hasThird = items.includes('第三方审图');
          const hasFile = items.includes('文件审核');
          if (hasSupplier && hasThird && hasFile) {
            return 'AI自动审核供应商图纸质量、第三方设计图纸、商务文件，确保资质合规性';
          }
          if (hasSupplier && hasThird) return '智能审核供应商与第三方图纸，确保设计质量';
          if (hasFile) return '自动审核商务文件合规性，降低资质风险';
        }
        if (aiType === 'aiGeneration' && items.includes('标书生成')) {
          return 'AI自动生成投标文件，结合企业资质与案例，提升标书质量与编制效率';
        }
        if (aiType === 'aiTools') {
          return '多语言翻译支持国际市场拓展，数据预处理规范化市场数据';
        }
      }

      // 工程造价部
      if (deptName === '工程造价部') {
        if (aiType === 'aiQuery' && items.includes('知识库')) {
          return '快速查询历史项目造价数据、定额标准等，为造价编制提供参考';
        }
        if (aiType === 'aiQueryNum') {
          const hasCost = items.includes('造价匡算');
          const hasPrice = items.includes('价格比对');
          const hasData = items.includes('运营数据问数');
          if (hasCost && hasPrice && hasData) {
            return 'AI智能匡算项目造价，自动比对历史价格，分析造价数据趋势，辅助成本控制决策';
          }
          if (hasCost && hasPrice) return '依据项目参数智能匡算造价，自动比对历史价格';
          if (hasCost) return '依据行业定额与项目参数，AI智能匡算项目造价';
          if (hasPrice) return '自动比对历史价格，识别造价异常';
          if (hasData) return '自然语言查询造价数据，快速获取分析结果';
        }
        if (aiType === 'aiTools') {
          return '多语言翻译支持国外造价标准查阅，数据预处理规范化造价数据';
        }
      }

      // 审计部
      if (deptName === '审计部') {
        if (aiType === 'aiQuery' && items.includes('知识库')) {
          return '快速查询历史审计记录、审计标准等，提升审计工作效率';
        }
        if (aiType === 'aiQueryNum' && items.includes('运营数据问数')) {
          return '自然语言查询运营数据，快速定位审计重点，提升审计发现能力';
        }
        if (aiType === 'aiReview' && items.includes('文件审核')) {
          return 'AI自动审核文件合规性，识别风险点，生成审计发现清单';
        }
        if (aiType === 'aiGeneration' && items.includes('报告生成')) {
          return '基于审计发现，AI自动生成审计报告，提升报告编制效率';
        }
        if (aiType === 'aiTools') {
          return '多语言翻译支持跨境审计，数据预处理规范化审计数据';
        }
      }

      // 质安部(HSE)
      if (deptName === '质安部(HSE)') {
        if (aiType === 'aiQuery' && items.includes('知识库')) {
          return '快速检索安全规范、环保标准等法规文件，确保合规性';
        }
        if (aiType === 'aiReview' && items.includes('文件审核')) {
          return 'AI自动审核安全文件合规性，识别安全隐患，降低安全风险';
        }
        if (aiType === 'aiGeneration' && items.includes('报告生成')) {
          return '基于安全检查结果，AI自动生成安全管理报告，提升报告质量';
        }
        if (aiType === 'aiTools') {
          return '多语言翻译支持国际安全标准查阅，数据预处理规范化安全数据';
        }
      }

      // 项目部
      if (deptName === '项目部') {
        if (aiType === 'aiQuery') {
          const hasKnowledge = items.includes('知识库');
          const hasSchedule = items.includes('施工进度辅助编制');
          if (hasKnowledge && hasSchedule) {
            return '知识库辅助施工方案编制，AI自动从图纸提取设备信息辅助施工进度跟踪';
          }
          if (hasKnowledge) return '快速检索施工规范、历史案例等，辅助施工方案编制';
          if (hasSchedule) return 'AI从图纸提取设备信息，辅助施工进度跟踪与管理';
        }
        if (aiType === 'aiQueryNum' && items.includes('运营数据问数')) {
          return '自然语言查询项目运营数据，快速获取项目进展信息';
        }
        if (aiType === 'aiTools') {
          return '多语言翻译支持国际项目，数据预处理规范化项目数据';
        }
      }

      // 工艺专业
      if (deptName === '工艺专业') {
        if (aiType === 'aiQuery') {
          const hasKnowledge = items.includes('知识库');
          const hasPackage = items.includes('工艺包助手');
          const hasDigital = items.includes('数字化交付');
          if (hasKnowledge && hasPackage && hasDigital) {
            return 'AI知识库快速检索工艺资料，工艺包助手从P&ID、规格书中提取信息，数字化交付平台智能查询工艺数据';
          }
          if (hasPackage && hasDigital) return '工艺包助手智能检索工艺数据，数字化交付平台实现工艺信息智能关联';
          if (hasPackage) return '从工艺包数据中快速检索设备参数、P&ID信息等工艺资料';
          if (hasDigital) return '基于数字化交付平台，智能查询工艺设计信息';
        }
        if (aiType === 'aiGeneration') {
          const hasBid = items.includes('标书生成');
          const hasEnv = items.includes('环境专篇');
          const hasSafe = items.includes('安全专篇');
          const hasReport = items.includes('报告生成');
          if (hasBid && hasEnv && hasSafe && hasReport) {
            return 'AI自动生成工艺标书、环境专篇、安全专篇及技术报告，大幅提升编制效率';
          }
          if (hasBid && hasEnv && hasSafe) return '自动生成工艺标书及环保、安全专篇';
          if (hasBid) return '基于工艺设计要求，自动生成标书中的工艺部分';
          if (hasEnv) return 'AI自动生成工艺专业的环境专篇';
          if (hasSafe) return 'AI自动生成工艺专业的安全专篇';
          if (hasReport) return '基于工艺设计数据，自动生成技术报告';
        }
        if (aiType === 'aiTools') {
          const hasMeeting = items.includes('会议听记');
          if (hasMeeting) {
            return '会议听记助手记录工艺评审会议，多语言翻译支持国外工艺资料，数据预处理规范化工艺数据';
          }
          return '多语言翻译支持国外工艺资料阅读，数据预处理规范化工艺数据';
        }
      }

      // 设备专业
      if (deptName === '设备专业') {
        if (aiType === 'aiQuery') {
          const hasKnowledge = items.includes('知识库');
          const hasDigital = items.includes('数字化交付');
          if (hasKnowledge && hasDigital) {
            return 'AI知识库快速查询设备参数、技术标准，数字化交付平台实现设备位号、图纸智能关联查询';
          }
          if (hasKnowledge) return '快速检索设备技术资料、标准规范等';
          if (hasDigital) return '基于数字化交付平台，智能查询设备相关信息';
        }
        if (aiType === 'aiReview' && items.includes('供应商图纸审核')) {
          return 'AI自动审核供应商设备图纸，检查设计符合性，确保设备选型准确';
        }
        if (aiType === 'aiTools') {
          const hasDrawing = items.includes('图纸比对');
          if (hasDrawing) {
            return 'AI自动比对设备图纸差异，多语言翻译支持进口设备资料，数据预处理规范化设备数据';
          }
          return '多语言翻译支持进口设备资料阅读，数据预处理规范化设备数据';
        }
      }

      // 结构专业
      if (deptName === '结构专业') {
        if (aiType === 'aiQuery') {
          const hasKnowledge = items.includes('知识库');
          const hasDigital = items.includes('数字化交付');
          if (hasKnowledge && hasDigital) {
            return 'AI知识库快速查询结构设计规范，数字化交付平台实现结构图纸智能关联查询';
          }
          if (hasKnowledge) return '快速检索结构设计规范、标准图集等';
          if (hasDigital) return '基于数字化交付平台，智能查询结构设计信息';
        }
        if (aiType === 'aiReview' && items.includes('第三方审图')) {
          return 'AI自动审核结构图纸，检查设计符合性，确保结构设计安全可靠';
        }
        if (aiType === 'aiTools') {
          const hasDrawing = items.includes('图纸比对');
          if (hasDrawing) {
            return 'AI自动比对结构图纸差异，多语言翻译支持国外规范阅读，数据预处理规范化结构数据';
          }
          return '多语言翻译支持国外结构规范阅读，数据预处理规范化结构数据';
        }
      }

      // 建筑专业
      if (deptName === '建筑专业') {
        if (aiType === 'aiQuery') {
          const hasKnowledge = items.includes('知识库');
          const hasDigital = items.includes('数字化交付');
          if (hasKnowledge && hasDigital) {
            return 'AI知识库快速查询建筑设计规范，数字化交付平台实现建筑图纸智能关联查询';
          }
          if (hasKnowledge) return '快速检索建筑设计规范、标准图集等';
          if (hasDigital) return '基于数字化交付平台，智能查询建筑设计信息';
        }
        if (aiType === 'aiReview' && items.includes('第三方审图')) {
          return 'AI自动审核建筑图纸，检查设计符合性，提升建筑设计质量';
        }
        if (aiType === 'aiTools') {
          const hasDrawing = items.includes('图纸比对');
          if (hasDrawing) {
            return 'AI自动比对建筑图纸差异，多语言翻译支持国外规范阅读，数据预处理规范化建筑数据';
          }
          return '多语言翻译支持国外建筑规范阅读，数据预处理规范化建筑数据';
        }
      }

      // 电仪
      if (deptName === '电仪') {
        if (aiType === 'aiQuery') {
          const hasKnowledge = items.includes('知识库');
          const hasDigital = items.includes('数字化交付');
          if (hasKnowledge && hasDigital) {
            return 'AI知识库快速查询电气仪表标准，数字化交付平台实现电仪图纸智能关联查询';
          }
          if (hasKnowledge) return '快速检索电气仪表设计规范、标准等';
          if (hasDigital) return '基于数字化交付平台，智能查询电仪设计信息';
        }
        if (aiType === 'aiTools') {
          const hasDrawing = items.includes('图纸比对');
          if (hasDrawing) {
            return 'AI自动比对电仪图纸差异，多语言翻译支持进口设备资料，数据预处理规范化电仪数据';
          }
          return '多语言翻译支持进口设备资料阅读，数据预处理规范化电仪数据';
        }
      }

      // 配管
      if (deptName === '配管') {
        if (aiType === 'aiQuery') {
          const hasKnowledge = items.includes('知识库');
          const hasDigital = items.includes('数字化交付');
          if (hasKnowledge && hasDigital) {
            return 'AI知识库快速检索管道设计规范，数字化交付平台实现配管图纸智能关联查询';
          }
          if (hasKnowledge) return '快速检索管道设计规范、标准等';
          if (hasDigital) return '基于数字化交付平台，智能查询配管设计信息';
        }
        if (aiType === 'aiTools') {
          const hasDrawing = items.includes('图纸比对');
          if (hasDrawing) {
            return 'AI自动比对配管图纸差异，多语言翻译支持国外规范阅读，数据预处理规范化配管数据';
          }
          return '多语言翻译支持国外管道规范阅读，数据预处理规范化配管数据';
        }
      }

      // 默认描述
      const itemsStr = items.join('、');
      const aiName = this.getAiTitle(aiType);
      return `${aiName}功能：${itemsStr}，助力${deptName}提升工作效率`;
    },
    showAiDetails(aiType) {
      this.selectedAi = aiType;
      this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${this.scrollPosition}px`;
      document.body.style.width = '100%';
    },
    closeAiDetails() {
      this.selectedAi = null;
      document.body.style.removeProperty('overflow');
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
      requestAnimationFrame(() => {
        window.scrollTo({
          top: this.scrollPosition,
          behavior: 'instant'
        });
      });
    }
  }
};
</script>

<style scoped>
.nx-departments {
  position: relative;
  padding: 100px 40px;
  background: linear-gradient(180deg, #fafbfc 0%, #f5f7fa 50%, #f8f9fa 100%);
  overflow: hidden;
}

.nx-departments::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 700px;
  background:
    radial-gradient(ellipse at top, rgba(59, 130, 246, 0.06) 0%, transparent 60%),
    radial-gradient(ellipse at 70% 20%, rgba(139, 92, 246, 0.05) 0%, transparent 50%);
  pointer-events: none;
  animation: deptTopFloat 15s ease-in-out infinite alternate;
}

@keyframes deptTopFloat {
  0% {
    opacity: 0.7;
    transform: translateY(0);
  }
  100% {
    opacity: 1;
    transform: translateY(-30px);
  }
}

.nx-departments-container {
  max-width: 1400px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.nx-departments-header {
  text-align: center;
  margin-bottom: 50px;
}

.nx-departments-subtitle {
  font-size: 1.2rem;
  color: var(--nx-blue-primary);
  font-weight: 500;
  letter-spacing: 0.3rem;
  text-transform: uppercase;
  margin-bottom: 0.3rem;
  text-shadow: 0 0 20px rgba(25, 118, 210, 0.25);
}

.nx-departments-title {
  font-size: clamp(32px, 4.5vw, 48px);
  font-weight: 800;
  color: var(--nx-text-title);
  margin-bottom: 12px;
  text-shadow: 0 0 30px rgba(13, 71, 161, 0.15),
    0 0 60px rgba(59, 130, 246, 0.1);
  position: relative;
  display: inline-block;
}

.nx-departments-title::after {
  content: "";
  position: absolute;
  bottom: -8px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 3px;
  background: linear-gradient(
    90deg,
    transparent,
    var(--nx-blue-primary),
    transparent
  );
  border-radius: 2px;
  box-shadow: 0 0 15px rgba(59, 130, 246, 0.6);
  animation: titleGlow 2s ease-in-out infinite;
}

.nx-departments-desc {
  font-size: 19px;
  line-height: 1.8;
  color: var(--nx-text-desc);
  max-width: 700px;
  margin: 0 auto;
  font-weight: 500;
}

/* AI解决方案网格布局 */
.nx-ai-solutions-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.nx-ai-solution-card {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 255, 0.9) 100%);
  backdrop-filter: blur(20px);
  border-radius: var(--nx-radius-lg);
  padding: 25px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.23, 1, 0.32, 1);
  border: 2px solid transparent;
  box-shadow: 0 8px 30px rgba(21, 101, 192, 0.12);
  position: relative;
  overflow: hidden;
}

.nx-ai-solution-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  border-radius: var(--nx-radius-lg) var(--nx-radius-lg) 0 0;
  transition: all 0.3s ease;
}

/* AI查询 - 蓝色 */
.nx-ai-solution-card.nx-ai-query {
  border-color: rgba(59, 130, 246, 0.2);
}

.nx-ai-solution-card.nx-ai-query::before {
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
}

.nx-ai-solution-card.nx-ai-query:hover {
  border-color: rgba(59, 130, 246, 0.4);
  box-shadow: 0 12px 40px rgba(59, 130, 246, 0.25);
  transform: translateY(-8px);
}

/* AI问数 - 青色 */
.nx-ai-solution-card.nx-ai-querynum {
  border-color: rgba(6, 182, 212, 0.2);
}

.nx-ai-solution-card.nx-ai-querynum::before {
  background: linear-gradient(90deg, #06b6d4, #22d3ee);
}

.nx-ai-solution-card.nx-ai-querynum:hover {
  border-color: rgba(6, 182, 212, 0.4);
  box-shadow: 0 12px 40px rgba(6, 182, 212, 0.25);
  transform: translateY(-8px);
}

/* AI审核 - 绿色 */
.nx-ai-solution-card.nx-ai-review {
  border-color: rgba(34, 197, 94, 0.2);
}

.nx-ai-solution-card.nx-ai-review::before {
  background: linear-gradient(90deg, #22c55e, #4ade80);
}

.nx-ai-solution-card.nx-ai-review:hover {
  border-color: rgba(34, 197, 94, 0.4);
  box-shadow: 0 12px 40px rgba(34, 197, 94, 0.25);
  transform: translateY(-8px);
}

/* AI生成 - 紫色 */
.nx-ai-solution-card.nx-ai-generation {
  border-color: rgba(139, 92, 246, 0.2);
}

.nx-ai-solution-card.nx-ai-generation::before {
  background: linear-gradient(90deg, #8b5cf6, #a78bfa);
}

.nx-ai-solution-card.nx-ai-generation:hover {
  border-color: rgba(139, 92, 246, 0.4);
  box-shadow: 0 12px 40px rgba(139, 92, 246, 0.25);
  transform: translateY(-8px);
}

/* AI工具 - 橙色 */
.nx-ai-solution-card.nx-ai-tools {
  border-color: rgba(249, 115, 22, 0.2);
}

.nx-ai-solution-card.nx-ai-tools::before {
  background: linear-gradient(90deg, #f97316, #fb923c);
}

.nx-ai-solution-card.nx-ai-tools:hover {
  border-color: rgba(249, 115, 22, 0.4);
  box-shadow: 0 12px 40px rgba(249, 115, 22, 0.25);
  transform: translateY(-8px);
}

.nx-ai-solution-icon {
  font-size: 36px;
  margin-bottom: 12px;
  display: block;
}

.nx-ai-solution-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--nx-text-title);
  margin: 0 0 10px 0;
}

.nx-ai-solution-desc {
  font-size: 13px;
  line-height: 1.5;
  color: var(--nx-text-desc);
  margin: 0 0 15px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.nx-ai-solution-departments {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  justify-content: center;
}

.nx-dept-tag {
  font-size: 11px;
  padding: 4px 10px;
  background: rgba(21, 101, 192, 0.08);
  border-radius: 12px;
  color: var(--nx-blue-primary);
  font-weight: 500;
}

/* 弹窗样式 */
.nx-ai-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.7);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-fade-enter-active {
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
}

.modal-fade-enter-from {
  opacity: 0;
}

.modal-fade-enter-from .nx-ai-modal {
  opacity: 0;
  transform: translateY(20px) scale(0.98);
}

.modal-fade-enter-to {
  opacity: 1;
}

.modal-fade-enter-to .nx-ai-modal {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.modal-fade-leave-active {
  transition: all 0.2s cubic-bezier(0.23, 1, 0.32, 1);
}

.modal-fade-leave-from {
  opacity: 1;
}

.modal-fade-leave-from .nx-ai-modal {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-leave-to .nx-ai-modal {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
}

.nx-ai-modal {
  background: linear-gradient(145deg,
    rgba(255, 255, 255, 0.98) 0%,
    rgba(248, 250, 255, 0.95) 100%);
  backdrop-filter: blur(30px);
  -webkit-backdrop-filter: blur(30px);
  border: 2px solid rgba(59, 130, 246, 0.15);
  border-radius: 16px;
  max-width: 800px;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow:
    0 20px 60px rgba(59, 130, 246, 0.2),
    0 0 80px rgba(59, 130, 246, 0.1);
}

/* AI查询弹窗 */
.nx-ai-modal.ai-query {
  border-color: rgba(59, 130, 246, 0.3);
}

.nx-ai-modal.ai-query .nx-ai-modal-header {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(96, 165, 250, 0.05) 100%);
  border-bottom-color: rgba(59, 130, 246, 0.2);
}

/* AI问数弹窗 */
.nx-ai-modal.ai-querynum {
  border-color: rgba(6, 182, 212, 0.3);
}

.nx-ai-modal.ai-querynum .nx-ai-modal-header {
  background: linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(34, 211, 238, 0.05) 100%);
  border-bottom-color: rgba(6, 182, 212, 0.2);
}

/* AI审核弹窗 */
.nx-ai-modal.ai-review {
  border-color: rgba(34, 197, 94, 0.3);
}

.nx-ai-modal.ai-review .nx-ai-modal-header {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(74, 222, 128, 0.05) 100%);
  border-bottom-color: rgba(34, 197, 94, 0.2);
}

/* AI生成弹窗 */
.nx-ai-modal.ai-generation {
  border-color: rgba(139, 92, 246, 0.3);
}

.nx-ai-modal.ai-generation .nx-ai-modal-header {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(167, 139, 250, 0.05) 100%);
  border-bottom-color: rgba(139, 92, 246, 0.2);
}

/* AI工具弹窗 */
.nx-ai-modal.ai-tools {
  border-color: rgba(249, 115, 22, 0.3);
}

.nx-ai-modal.ai-tools .nx-ai-modal-header {
  background: linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(251, 146, 60, 0.05) 100%);
  border-bottom-color: rgba(249, 115, 22, 0.2);
}

/* AI查询弹窗 - 蓝色 */
.nx-ai-modal.ai-query {
  border-color: rgba(59, 130, 246, 0.3);
}

.nx-ai-modal.ai-query .nx-ai-modal-header {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(96, 165, 250, 0.05) 100%);
  border-bottom-color: rgba(59, 130, 246, 0.2);
}

.nx-ai-modal.ai-query .nx-ai-modal-title {
  color: #3b82f6;
}

.nx-ai-modal.ai-query .nx-ai-modal-description {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(96, 165, 250, 0.04) 100%);
  border-left-color: #3b82f6;
}

.nx-ai-modal.ai-query .nx-ai-dept-list-title {
  color: #3b82f6;
}

.nx-ai-modal.ai-query .nx-ai-dept-card {
  border-color: rgba(59, 130, 246, 0.15);
}

.nx-ai-modal.ai-query .nx-ai-dept-card:hover {
  border-color: rgba(59, 130, 246, 0.3);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
}

/* AI问数弹窗 - 青色 */
.nx-ai-modal.ai-querynum {
  border-color: rgba(6, 182, 212, 0.3);
}

.nx-ai-modal.ai-querynum .nx-ai-modal-header {
  background: linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(34, 211, 238, 0.05) 100%);
  border-bottom-color: rgba(6, 182, 212, 0.2);
}

.nx-ai-modal.ai-querynum .nx-ai-modal-title {
  color: #06b6d4;
}

.nx-ai-modal.ai-querynum .nx-ai-modal-description {
  background: linear-gradient(135deg, rgba(6, 182, 212, 0.08) 0%, rgba(34, 211, 238, 0.04) 100%);
  border-left-color: #06b6d4;
}

.nx-ai-modal.ai-querynum .nx-ai-dept-list-title {
  color: #06b6d4;
}

.nx-ai-modal.ai-querynum .nx-ai-dept-card {
  border-color: rgba(6, 182, 212, 0.15);
}

.nx-ai-modal.ai-querynum .nx-ai-dept-card:hover {
  border-color: rgba(6, 182, 212, 0.3);
  box-shadow: 0 8px 25px rgba(6, 182, 212, 0.15);
}

/* AI审核弹窗 - 绿色 */
.nx-ai-modal.ai-review .nx-ai-modal-title {
  color: #22c55e;
}

.nx-ai-modal.ai-review .nx-ai-modal-description {
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.08) 0%, rgba(74, 222, 128, 0.04) 100%);
  border-left-color: #22c55e;
}

.nx-ai-modal.ai-review .nx-ai-dept-list-title {
  color: #22c55e;
}

.nx-ai-modal.ai-review .nx-ai-dept-card {
  border-color: rgba(34, 197, 94, 0.15);
}

.nx-ai-modal.ai-review .nx-ai-dept-card:hover {
  border-color: rgba(34, 197, 94, 0.3);
  box-shadow: 0 8px 25px rgba(34, 197, 94, 0.15);
}

/* AI生成弹窗 - 紫色 */
.nx-ai-modal.ai-generation .nx-ai-modal-title {
  color: #8b5cf6;
}

.nx-ai-modal.ai-generation .nx-ai-modal-description {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(167, 139, 250, 0.04) 100%);
  border-left-color: #8b5cf6;
}

.nx-ai-modal.ai-generation .nx-ai-dept-list-title {
  color: #8b5cf6;
}

.nx-ai-modal.ai-generation .nx-ai-dept-card {
  border-color: rgba(139, 92, 246, 0.15);
}

.nx-ai-modal.ai-generation .nx-ai-dept-card:hover {
  border-color: rgba(139, 92, 246, 0.3);
  box-shadow: 0 8px 25px rgba(139, 92, 246, 0.15);
}

/* AI工具弹窗 - 橙色 */
.nx-ai-modal.ai-tools .nx-ai-modal-title {
  color: #f97316;
}

.nx-ai-modal.ai-tools .nx-ai-modal-description {
  background: linear-gradient(135deg, rgba(249, 115, 22, 0.08) 0%, rgba(251, 146, 60, 0.04) 100%);
  border-left-color: #f97316;
}

.nx-ai-modal.ai-tools .nx-ai-dept-list-title {
  color: #f97316;
}

.nx-ai-modal.ai-tools .nx-ai-dept-card {
  border-color: rgba(249, 115, 22, 0.15);
}

.nx-ai-modal.ai-tools .nx-ai-dept-card:hover {
  border-color: rgba(249, 115, 22, 0.3);
  box-shadow: 0 8px 25px rgba(249, 115, 22, 0.15);
}

.nx-ai-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(59, 130, 246, 0.15);
  transition: all 0.3s ease;
}

.nx-ai-modal-title-wrapper {
  display: flex;
  align-items: center;
  gap: 12px;
}

.nx-ai-modal-icon {
  font-size: 28px;
}

.nx-ai-modal-title {
  font-size: 22px;
  font-weight: 700;
  color: var(--nx-text-title);
  margin: 0;
}

.nx-ai-modal-close {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  flex-shrink: 0;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
}

.nx-ai-modal-close:hover {
  background: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  transform: rotate(90deg);

  svg {
    color: #ef4444;
  }
}

.nx-ai-modal-close svg {
  width: 18px;
  height: 18px;
  color: #ef4444;
}

.nx-ai-modal-content {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.nx-ai-modal-description {
  padding: 16px;
  margin-bottom: 24px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(96, 165, 250, 0.04) 100%);
  border-radius: 12px;
  border-left: 4px solid var(--nx-blue-primary);
}

.nx-ai-modal-description p {
  font-size: 15px;
  line-height: 1.7;
  color: var(--nx-text-content);
  margin: 0;
  text-align: center;
}

.nx-ai-dept-list-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--nx-text-title);
  margin: 0 0 20px 0;
  text-align: center;
}

.nx-ai-dept-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.nx-ai-dept-card {
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 255, 0.85) 100%);
  border: 1.5px solid rgba(21, 101, 192, 0.15);
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
}

.nx-ai-dept-card:hover {
  border-color: rgba(21, 101, 192, 0.3);
  box-shadow: 0 4px 20px rgba(21, 101, 192, 0.12);
}

.nx-ai-dept-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--nx-blue-primary);
  margin: 0 0 8px 0;
}

.nx-ai-dept-desc {
  font-size: 13px;
  line-height: 1.5;
  color: var(--nx-text-desc);
  margin: 0 0 12px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.nx-ai-dept-items {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.nx-ai-dept-item {
  font-size: 12px;
  padding: 4px 10px;
  background: rgba(59, 130, 246, 0.1);
  border-radius: 12px;
  color: var(--nx-blue-primary);
  font-weight: 500;
}

/* 自定义滚动条 */
.nx-ai-modal-content::-webkit-scrollbar {
  width: 3px !important;
}

.nx-ai-modal-content::-webkit-scrollbar-track {
  background: transparent !important;
}

.nx-ai-modal-content::-webkit-scrollbar-thumb {
  background: #999999 !important;
  border-radius: 3px !important;
}

.nx-ai-modal-content::-webkit-scrollbar-thumb:hover {
  background: #777777 !important;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .nx-ai-solutions-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .nx-departments {
    padding: 70px 20px;
  }

  .nx-departments-header {
    margin-bottom: 40px;
  }

  .nx-departments-subtitle {
    font-size: 1rem;
    letter-spacing: 0.2rem;
  }

  .nx-departments-title {
    font-size: clamp(28px, 5vw, 36px);
  }

  .nx-departments-desc {
    font-size: 15px;
  }

  .nx-ai-solutions-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .nx-ai-solution-card {
    padding: 20px 15px;
  }

  .nx-ai-solution-icon {
    font-size: 28px;
  }

  .nx-ai-solution-title {
    font-size: 16px;
  }

  .nx-ai-solution-desc {
    font-size: 12px;
  }

  .nx-dept-tag {
    font-size: 10px;
    padding: 3px 8px;
  }

  /* 弹窗响应式 */
  .nx-ai-modal {
    max-width: 92vw;
    max-height: 85vh;
  }

  .nx-ai-modal-header {
    padding: 16px 20px;
  }

  .nx-ai-modal-icon {
    font-size: 24px;
  }

  .nx-ai-modal-title {
    font-size: 18px;
  }

  .nx-ai-modal-content {
    padding: 20px;
  }

  .nx-ai-modal-description p {
    font-size: 14px;
  }

  .nx-ai-dept-cards {
    grid-template-columns: 1fr;
  }

  .nx-ai-dept-card {
    padding: 14px;
  }

  .nx-ai-dept-name {
    font-size: 15px;
  }

  .nx-ai-dept-desc {
    font-size: 12px;
  }

  .nx-ai-dept-item {
    font-size: 11px;
    padding: 3px 8px;
  }
}

@media (max-width: 480px) {
  .nx-ai-solutions-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .nx-ai-modal {
    border-radius: 14px;
  }

  .nx-ai-modal-header {
    padding: 14px 16px;
  }

  .nx-ai-modal-icon {
    font-size: 22px;
  }

  .nx-ai-modal-title {
    font-size: 16px;
  }

  .nx-ai-modal-content {
    padding: 16px;
  }

  .nx-ai-modal-description {
    padding: 12px;
    margin-bottom: 16px;
  }

  .nx-ai-modal-description p {
    font-size: 13px;
  }

  .nx-ai-dept-list-title {
    font-size: 16px;
  }

  .nx-ai-dept-card {
    padding: 12px;
  }

  .nx-ai-dept-name {
    font-size: 14px;
  }

  .nx-ai-dept-desc {
    font-size: 11px;
  }
}

/* 标题光效动画 */
@keyframes titleGlow {
  0%, 100% {
    width: 60px;
    opacity: 0.6;
  }
  50% {
    width: 120px;
    opacity: 1;
  }
}
</style>

<style>
/* 滚动条样式 - 非scoped以确保生效 */
.nx-ai-modal .nx-ai-modal-content::-webkit-scrollbar,
.nx-ai-modal.ai-querynum .nx-ai-modal-content::-webkit-scrollbar,
.nx-ai-modal .nx-gray-scrollbar::-webkit-scrollbar,
.nx-gray-scrollbar::-webkit-scrollbar {
  width: 3px !important;
}

.nx-ai-modal .nx-ai-modal-content::-webkit-scrollbar-track,
.nx-ai-modal.ai-querynum .nx-ai-modal-content::-webkit-scrollbar-track,
.nx-ai-modal .nx-gray-scrollbar::-webkit-scrollbar-track,
.nx-gray-scrollbar::-webkit-scrollbar-track {
  background: transparent !important;
}

.nx-ai-modal .nx-ai-modal-content::-webkit-scrollbar-thumb,
.nx-ai-modal.ai-querynum .nx-ai-modal-content::-webkit-scrollbar-thumb,
.nx-ai-modal .nx-gray-scrollbar::-webkit-scrollbar-thumb,
.nx-gray-scrollbar::-webkit-scrollbar-thumb {
  background: #999999 !important;
  border-radius: 3px !important;
}

.nx-ai-modal .nx-ai-modal-content::-webkit-scrollbar-thumb:hover,
.nx-ai-modal.ai-querynum .nx-ai-modal-content::-webkit-scrollbar-thumb:hover,
.nx-ai-modal .nx-gray-scrollbar::-webkit-scrollbar-thumb:hover,
.nx-gray-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #777777 !important;
}

/* Firefox 滚动条样式 */
.nx-ai-modal .nx-ai-modal-content,
.nx-ai-modal.ai-querynum .nx-ai-modal-content,
.nx-ai-modal .nx-gray-scrollbar,
.nx-gray-scrollbar {
  scrollbar-width: thin !important;
  scrollbar-color: #999999 transparent !important;
}

.nx-ai-modal .nx-ai-modal-content:hover,
.nx-ai-modal.ai-querynum .nx-ai-modal-content:hover,
.nx-ai-modal .nx-gray-scrollbar:hover,
.nx-gray-scrollbar:hover {
  scrollbar-color: #777777 transparent !important;
}
</style>
